/**
 * Extracción de texto plano de un EPUB para indexación de búsqueda.
 *
 * Recorre el `<spine>` del OPF en orden de lectura, resuelve cada `itemref`
 * al `<item>` del `<manifest>`, lee su XHTML y extrae el texto visible con
 * `DOMParser` + `body.textContent`. Reutiliza los helpers de `parser.ts`
 * (abrir zip, localizar OPF, leer texto, resolver rutas relativas) para no
 * duplicar la lógica de navegación del zip.
 *
 * Módulo profundo y puro salvo por la I/O del zip: testeable con un fixture
 * generado por JSZip.
 */

import { openZip, findOpfPath, readText, resolveRelativePath } from './parser'
import { InvalidEpubError } from './parser'

/** Texto extraído de un capítulo del spine, con su href para contexto. */
export interface ChapterText {
  /** href del item del manifest (resuelto respecto a la raíz del zip). */
  chapterHref: string
  /** Índice ordinal del capítulo en el `<spine>` (0-based). */
  spineIndex: number
  /** Texto visible saneado (sin tags, espacios colapsados). */
  text: string
}

const domParser = new DOMParser()

/**
 * Extrae el texto de todos los capítulos del spine de un EPUB, en orden
 * de lectura.
 *
 * @throws {InvalidEpubError} si la estructura del EPUB es inválida.
 */
export async function extractBookText(blob: Blob): Promise<ChapterText[]> {
  const zip = await openZip(blob)
  const opfPath = await findOpfPath(zip)
  const opfText = await readText(zip, opfPath)
  const doc = domParser.parseFromString(opfText, 'application/xml')

  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new InvalidEpubError(`OPF mal formado en "${opfPath}"`)
  }

  // Índice del manifest: id → { href, media-type }. Namespace-agnostic
  // (el OPF usa el namespace opf por defecto, pero `getElementsByTagName`
  // sin namespace funciona porque ignoramos el prefijo en el match).
  const manifestItems = getElementsByTagName(doc, 'item')
  const manifestById = new Map<string, { href: string; mediaType: string }>()
  for (const item of manifestItems) {
    const id = item.getAttribute('id')
    const href = item.getAttribute('href')
    const mediaType = item.getAttribute('media-type') ?? ''
    if (id && href) manifestById.set(id, { href, mediaType })
  }

  // Recorrer el spine en orden. El índice del bucle es el `spineIndex`
  // (posición ordinal en el spine), que coincide con el que foliate usa
  // para `sections[i]` y para `CFI.fake.fromIndex(i)`. Se cuentan también
  // los itemref saltados (linear="no", no-XHTML), porque foliate los incluye
  // en su modelo de secciones.
  const itemrefs = getElementsByTagName(doc, 'itemref')
  const chapters: ChapterText[] = []

  for (let spineIndex = 0; spineIndex < itemrefs.length; spineIndex++) {
    const itemref = itemrefs[spineIndex]
    const idref = itemref.getAttribute('idref')
    if (!idref) continue
    // linear="no" indica contenido no lineal (p. ej. notas); lo saltamos
    // para extraer texto, pero el spineIndex se conserva para alineación CFI.
    const linear = itemref.getAttribute('linear')
    if (linear === 'no') continue

    const item = manifestById.get(idref)
    if (!item) continue
    // Solo indexamos contenido XHTML/HTML (no imágenes, CSS, NCX...).
    if (!isXhtml(item.mediaType)) continue

    const resolvedHref = resolveRelativePath(item.href, opfPath).split('#')[0]
    const entry = zip.file(resolvedHref)
    if (!entry) continue

    const xhtml = await entry.async('text')
    const text = extractVisibleText(xhtml)
    if (text.trim().length === 0) continue

    chapters.push({ chapterHref: resolvedHref, spineIndex, text })
  }

  return chapters
}

/** ¿Es un tipo de contenido XHTML/HTML indexable? */
function isXhtml(mediaType: string): boolean {
  const mt = mediaType.toLowerCase()
  return (
    mt === 'application/xhtml+xml' ||
    mt === 'text/html' ||
    mt === 'application/xml'
  )
}

/**
 * Parsea XHTML y devuelve el texto visible, saneando espacios.
 * Quita explícitamente `<script>` y `<style>` por seguridad/limpieza
 * aunque `textContent` ya no ejecuta scripts.
 */
function extractVisibleText(xhtml: string): string {
  const doc = domParser.parseFromString(xhtml, 'application/xhtml+xml')
  // Eliminar script/style antes de leer textContent.
  for (const tag of ['script', 'style', 'head']) {
    const els = doc.getElementsByTagName(tag)
    // Collection viva: iterar de atrás hacia adelante al borrar.
    for (let i = els.length - 1; i >= 0; i--) {
      els[i].parentNode?.removeChild(els[i])
    }
  }
  const raw = doc.body?.textContent ?? doc.documentElement?.textContent ?? ''
  return collapseWhitespace(raw)
}

/** Colapsa secuencias de espacios en blanco (incl. saltos) a un solo espacio. */
export function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Busca elementos por localName sin depender del namespace del OPF.
 * Mismo truco namespace-agnostic que `metadata.ts:getByLocalName`.
 */
function getElementsByTagName(doc: Document, localName: string): Element[] {
  const all = doc.getElementsByTagName('*')
  const out: Element[] = []
  for (let i = 0; i < all.length; i++) {
    const el = all[i]
    const ln = el.localName ?? el.nodeName.split(':').pop() ?? ''
    if (ln.toLowerCase() === localName) out.push(el)
  }
  return out
}
